import { useCallback, useEffect, useRef, useState } from "react";
import { LuLock } from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import DateFilter from "../../app-components/ui/DateFilter";
import EmptyState from "../../app-components/ui/EmptyState";
import ExportButton from "../../app-components/ui/ExportButton";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination, { PER_PAGE_OPTIONS } from "../../app-components/ui/Pagination";
import { btnPrimary, card, inputBase } from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import { getErrorMessage } from "../../utils";
import { downloadCsvExport } from "../../utils/exportCsv";

type Entry = {
  id: number;
  actor: string;
  admin_email: string | null;
  action: string;
  action_label: string;
  summary: string;
  ip: string | null;
  device: string | null;
  created_at: string;
};
type Option = { value: string; label: string };

// action key prefix -> group label, in display order
const GROUPS: Record<string, string> = {
  auth: "Sign-in",
  account: "Own account",
  member: "Members",
  subscription: "Subscriptions",
  request: "Requests",
  price: "Prices",
  broadcast: "Broadcasts",
  export: "Exports",
  admin: "Admins",
  maintenance: "Server",
};

// colour per area, so the log scans quickly
const TONE: Record<string, string> = {
  auth: "bg-slate-100 text-slate-700",
  account: "bg-slate-100 text-slate-700",
  member: "bg-hc_F4EBFA text-honeConnectPurple",
  subscription: "bg-emerald-50 text-emerald-700",
  request: "bg-blue-50 text-blue-700",
  price: "bg-amber-50 text-amber-700",
  broadcast: "bg-pink-50 text-pink-700",
  export: "bg-cyan-50 text-cyan-700",
  admin: "bg-red-50 text-red-700",
  maintenance: "bg-red-50 text-red-700",
};

const th = "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm align-top";

const PER_PAGE_KEY = "hc_per_page_audit";
const readPerPage = () => {
  try {
    const n = Number(localStorage.getItem(PER_PAGE_KEY));
    return PER_PAGE_OPTIONS.includes(n) ? n : PER_PAGE_OPTIONS[0];
  } catch {
    return PER_PAGE_OPTIONS[0];
  }
};

const fmtWhen = (iso: string) => {
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z"); // stored in UTC
  return {
    date: d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
};

const AuditLog = () => {
  const [rows, setRows] = useState<Entry[]>([]);
  const [actions, setActions] = useState<Option[]>([]);
  const [admins, setAdmins] = useState<{ id: number; name: string }[]>([]);
  const [adminId, setAdminId] = useState("");
  const [action, setAction] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [dateError, setDateError] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(readPerPage);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    apiClient
      .get("audit-log/options")
      .then((res: any) => {
        setActions(res?.data?.actions ?? []);
        setAdmins(res?.data?.admins ?? []);
      })
      .catch(() => undefined);
  }, []);

  const params = useCallback(
    () => ({ page, per_page: perPage, admin_id: adminId, action, search, dateRange }),
    [page, perPage, adminId, action, search, dateRange]
  );

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    try {
      const res: any = await apiClient.get("audit-log", { params: params() });
      if (mine !== seq.current) return;
      const body = res?.data ?? {};
      setRows(body.data ?? []);
      setLastPage(Number(body.last_page) || 0);
      setTotal(typeof body.total === "number" ? body.total : undefined);
    } catch (err) {
      if (mine !== seq.current) return;
      setError(getErrorMessage(err));
      setRows([]);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setDateError("");
    if (startDate && endDate) {
      if (new Date(endDate) >= new Date(startDate)) {
        setDateRange(`${startDate},${endDate}`);
        setPage(1);
      } else setDateError("End date must be on or after the start date.");
    }
    if (!startDate && !endDate) setDateRange("");
  }, [startDate, endDate]);

  const grouped = Object.entries(GROUPS)
    .map(([prefix, label]) => ({ prefix, label, items: actions.filter((a) => a.value.startsWith(prefix + ".")) }))
    .filter((g) => g.items.length);

  const changePerPage = (n: number) => {
    try {
      localStorage.setItem(PER_PAGE_KEY, String(n));
    } catch {
      /* not remembered */
    }
    setPerPage(n);
    setPage(1);
  };

  const hasFilters = !!(adminId || action || search || dateRange);
  const clear = () => {
    setAdminId("");
    setAction("");
    setSearchInput("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  return (
    <AdminLayout title="Admin | Audit log">
      <PageHeader
        title="Audit log"
        subtitle="Who did what, and when. Entries can't be edited or deleted."
        actions={
          <ExportButton
            total={total}
            noun="entries"
            disabled={loading}
            onExport={() => downloadCsvExport("audit-log/export", params(), "audit-log", total, "entries")}
          />
        }
      />

      <div className={`${card} py-5`}>
        <div className="mb-4 flex flex-wrap items-center gap-3 px-5">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search the audit log"
            placeholder="Search summaries, admin or IP"
            className={`${inputBase} min-w-[220px] flex-1`}
          />
          <select aria-label="Admin" value={adminId} onChange={(e) => { setAdminId(e.target.value); setPage(1); }} className={inputBase}>
            <option value="">All admins</option>
            {admins.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
            <option value="server">Server (maintenance / commands)</option>
          </select>
          <select aria-label="Action" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className={inputBase}>
            <option value="">All actions</option>
            {grouped.map((g) => (
              <optgroup key={g.prefix} label={g.label}>
                <option value={g.prefix}>All {g.label.toLowerCase()}</option>
                {g.items.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="mb-4 px-5">
          <DateFilter startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />
          {dateError && <p role="alert" className="mt-2 text-sm text-red-600">{dateError}</p>}
        </div>

        {error && !loading ? (
          <div className="flex flex-col items-center border-y border-hcBorder px-6 py-14 text-center">
            <p className="mb-1 font-semibold text-honeConnectBlack">Couldn't load the audit log</p>
            <p className="mb-4 max-w-md text-sm text-hcMuted">{error}</p>
            <button onClick={load} className={btnPrimary}>Try again</button>
          </div>
        ) : (
          <div className="overflow-x-auto border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className={th}>When</th>
                  <th className={th}>Admin</th>
                  <th className={th}>Action</th>
                  <th className={th}>What happened</th>
                  <th className={th}>From</th>
                </tr>
              </thead>
              {loading ? (
                <TableSkeleton columns={5} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {rows.map((r) => {
                    const when = fmtWhen(r.created_at);
                    const area = r.action.split(".")[0];
                    return (
                      <tr key={r.id} className="border-b border-hcBorder hover:bg-hcBg">
                        <td className={`${td} whitespace-nowrap`}>
                          <span className="block">{when.date}</span>
                          <span className="text-xs text-hcMuted">{when.time}</span>
                        </td>
                        <td className={`${td} whitespace-nowrap`}>
                          <span className="block font-medium">{r.actor}</span>
                          {r.admin_email && <span className="text-xs text-hcMuted">{r.admin_email}</span>}
                        </td>
                        <td className={`${td} whitespace-nowrap`}>
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE[area] ?? "bg-slate-100 text-slate-700"}`}>
                            {r.action_label}
                          </span>
                        </td>
                        <td className={`${td} min-w-[280px] max-w-[520px]`}>{r.summary}</td>
                        <td className={`${td} whitespace-nowrap text-xs text-hcMuted`}>
                          {r.device && <span className="block">{r.device}</span>}
                          {r.ip}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
            {!loading && rows.length === 0 && (
              <EmptyState
                title={hasFilters ? "No entries match these filters" : "No entries yet"}
                message={hasFilters ? "Try changing the filters." : "Admin actions will be recorded here from now on."}
                onClear={hasFilters ? clear : undefined}
              />
            )}
          </div>
        )}

        {rows.length > 0 && (
          <Pagination currentPage={page} lastPage={lastPage} total={total} onPageChange={setPage} perPage={perPage} onPerPageChange={changePerPage} />
        )}

        <p className="mt-4 flex items-center gap-2 px-5 text-xs text-hcMuted">
          <LuLock className="h-3.5 w-3.5" aria-hidden />
          Times are shown in your device's time zone. Passwords and codes are never recorded.
        </p>
      </div>
    </AdminLayout>
  );
};

export default AuditLog;
